import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SiteLayout } from "@/components/site/Layout";
import { api, gbp, longDate } from "@/lib/api";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("checking");
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setState("paid");
          api.get(`/me/bookings/session/${sid}`).then((r) => setBooking(r.data)).catch(() => {});
          return;
        }
        if (data.payment_status === "expired") { setState("failed"); return; }
      } catch (e) { /* retry */ }
      tries += 1;
      if (tries < 15) timer = setTimeout(poll, 2000); else setState("timeout");
    };
    if (sid) poll(); else setState("failed");
    return () => clearTimeout(timer);
  }, [sid]);

  return (
    <SiteLayout>
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="payment-success-page">
        {state === "checking" && <><Loader2 className="h-10 w-10 mx-auto animate-spin text-primary" /><p className="mt-4" data-testid="payment-status-checking">Confirming your payment…</p></>}
        {state === "paid" && (
          <div className="fade-up space-y-4">
            <CheckCircle2 className="h-14 w-14 mx-auto text-primary" />
            <h1 className="text-3xl font-extrabold tracking-tight" data-testid="payment-status-paid">You're booked in</h1>
            {booking && (
              <div className="rounded-2xl border border-border bg-card p-6 text-left text-sm space-y-1.5" data-testid="booking-confirmation-details">
                <p className="font-display font-bold text-base">{booking.service_name}</p>
                <p>{booking.clinic_name} · {booking.practitioner_name}</p>
                <p>{longDate(booking.date)} at {booking.start}</p>
                <p className="font-semibold">Paid {gbp(booking.amount)}</p>
                <p className="text-muted-foreground">Ref {booking.id.slice(0, 8).toUpperCase()} — a confirmation email is on its way.</p>
              </div>
            )}
            <Button className="rounded-full" asChild data-testid="view-my-bookings-button"><Link to="/account">View my bookings</Link></Button>
          </div>
        )}
        {(state === "failed" || state === "timeout") && (
          <div className="space-y-4">
            <XCircle className="h-12 w-12 mx-auto text-rose-500" />
            <p data-testid="payment-status-failed">{state === "timeout" ? "We couldn't confirm your payment yet. Check My bookings in a minute." : "Payment was not completed."}</p>
            <Button variant="outline" className="rounded-full" asChild><Link to="/account">My bookings</Link></Button>
          </div>
        )}
      </div>
    </SiteLayout>
  );
}

export function PaymentCancel() {
  return (
    <SiteLayout>
      <div className="max-w-xl mx-auto px-4 py-20 text-center space-y-4" data-testid="payment-cancel-page">
        <XCircle className="h-12 w-12 mx-auto text-accent" />
        <h1 className="text-3xl font-extrabold tracking-tight">Payment cancelled</h1>
        <p className="text-muted-foreground">No money was taken. Your slot is held for a short while if you'd like to try again.</p>
        <Button className="rounded-full" asChild data-testid="payment-cancel-browse-button"><Link to="/clinics">Back to clinics</Link></Button>
      </div>
    </SiteLayout>
  );
}
